import React from 'react';
import { CloudOff, Loader2 } from 'lucide-react';
import { useSync } from '../contexts/SyncContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * iter355 — surfaces the offline write queue.
 *
 * Everything the app logs is saved locally first, so with no signal the
 * screens look perfectly normal — which is exactly why the user needs to be
 * told that the cloud copy is still behind. Tapping retries immediately.
 */
export function OfflineQueueBanner() {
  const { queuedWrites, retryQueuedWrites, isOnline } = useSync();
  const { user } = useAuth();
  const [retrying, setRetrying] = React.useState(false);
  // The cookie card is pinned bottom-centre on mobile; sitting on top of its
  // buttons made them untappable, so lift the pill while it is showing.
  const [cookieBannerUp, setCookieBannerUp] = React.useState(
    () => { try { return !localStorage.getItem('fitness_cookie_consent'); } catch { return false; } },
  );

  React.useEffect(() => {
    if (queuedWrites === 0) setRetrying(false);
  }, [queuedWrites]);

  React.useEffect(() => {
    if (!cookieBannerUp) return;
    const t = setInterval(() => {
      try {
        if (localStorage.getItem('fitness_cookie_consent')) setCookieBannerUp(false);
      } catch { /* noop */ }
    }, 1000);
    return () => clearInterval(t);
  }, [cookieBannerUp]);

  // Queued writes belong to an account — a logged-out visitor can do nothing
  // about them and tapping would only 401 (iter357).
  if (queuedWrites === 0 || !user) return null;

  const retry = () => {
    if (!isOnline) return;
    setRetrying(true);
    retryQueuedWrites();
  };

  return (
    <button
      type="button"
      onClick={retry}
      data-testid="offline-queue-banner"
      className={`fixed left-1/2 z-[10000] inline-flex -translate-x-1/2 items-center gap-2 rounded-full border border-yellow-500/40 bg-[#1b1f27]/95 px-3 py-2 text-yellow-300 shadow-lg backdrop-blur transition-colors hover:bg-[#232833] active:scale-[0.98] ${cookieBannerUp ? 'bottom-[21rem] sm:bottom-24' : 'bottom-24'}`}
      title={isOnline ? 'Tap to retry now' : 'Will upload automatically when you are back online'}
    >
      {retrying && isOnline
        ? <Loader2 className="h-4 w-4 animate-spin" />
        : <CloudOff className="h-4 w-4" />}
      <span className="text-xs font-medium" data-testid="offline-queue-count">
        {queuedWrites} {queuedWrites === 1 ? 'change' : 'changes'} waiting to sync
      </span>
    </button>
  );
}

export default OfflineQueueBanner;
